import React, { ReactNode } from 'react';
import classNames from 'classnames';

import { TermTooltip } from '~components';
import WholeMoney from '~support-at-home/components/common/WholeMoney';

import Swatch from './Swatch';

export interface TrackerCaption {
  /** Unique on the page: becomes the DOM id `term-tooltip-tracker-{id}`. */
  id: string;
  /** What the label means, shown on hover and keyboard focus of the underlined label. */
  content: ReactNode;
}

export interface TrackerLegendItem {
  key: string;
  label: string;
  /** A number is shown as signed whole dollars, exact on hover; a string is shown as given (for example a rate). Omit for a key only. */
  value?: number | string;
  /** Swatch colour. Omit for rows that are not a chart key (for example Utilisation). */
  color?: string;
  /** Opt-in: the label becomes a dotted-underline term (TermTooltip) that explains itself. */
  caption?: TrackerCaption;
  /** The value is an amount over a limit: at least $1 when above zero. */
  over?: boolean;
  /** Smaller lines indented under the row with a tree line to it, each a part of its amount, e.g. Booked and Claimed. */
  subItems?: TrackerLegendSubItem[];
}

export interface TrackerLegendSubItem {
  key: string;
  label: string;
  value: number;
}

interface ComponentProps {
  items: TrackerLegendItem[];
  className?: string;
}

/**
 * Legend rows: colour swatch, label (an explained term when it has a caption) and signed value. A value below zero
 * gets the danger class so it reads as a warning wherever it appears. A row's sub items follow it as smaller,
 * indented rows.
 */
const TrackerLegend: React.FC<ComponentProps> = ({ items, className }) => (
  <ul className={['tracker-legend', className].filter(Boolean).join(' ')}>
    {items.map(({ key, label, value, color, caption, over = false, subItems = [] }) => {
      const isAlert = typeof value === 'number' && value < 0;
      return (
        <React.Fragment key={key}>
          <li className="tracker-legend-row" data-cy={`tracker-legend-${key}`}>
            <span className="tracker-legend-label">
              {color && <Swatch color={color} />}
              {caption ? (
                <TermTooltip id={`tracker-${caption.id}`} label={label} content={caption.content} />
              ) : (
                <span>{label}</span>
              )}
            </span>
            {value !== undefined && (
              <strong
                className={isAlert ? 'tracker-legend-value tracker-legend-value--negative' : 'tracker-legend-value'}
              >
                {typeof value === 'number' ? <WholeMoney value={value} over={over} /> : value}
              </strong>
            )}
          </li>
          {subItems.map((sub, index) => (
            <li
              key={`${key}-${sub.key}`}
              className={classNames('tracker-legend-row', 'tracker-legend-row--sub', {
                'tracker-legend-row--sub-last': index === subItems.length - 1,
              })}
              data-cy={`tracker-legend-${key}-${sub.key}`}
            >
              <span className="tracker-legend-label">{sub.label}</span>
              <span className="tracker-legend-value">
                <WholeMoney value={sub.value} over />
              </span>
            </li>
          ))}
        </React.Fragment>
      );
    })}
  </ul>
);

export default TrackerLegend;
